import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { fmt$, fmt$0 } from '../utils/formatters';
import { getVendorCategory, resolveVendor, findSimilarVendors, assignVendorAlias, loadAllVendorsForUI } from '../lib/vendors';
import InvoiceReview from './InvoiceReview';
import InvoiceOriginalViewer from './InvoiceOriginalViewer';
import { suggestEC } from '../lib/feeDetection';
import { postProcessExtraction } from '../lib/invoiceApi';
import { normalizeVendor } from '../lib/invoiceUtils';
import { sharedConfirmInvoice } from '../lib/confirmInvoice';

export default function InvoiceReviewPage({taskId,orgId,uid,ings,setIngs,mi,batches,bc,settings,save,onConfirm,setOtherExp,t,si,sb,sh,sd,mn,setPg,refreshBell,setReviewTaskId}){
const[staging,setStaging]=useState(null);
const[task,setTask]=useState(null);
const[loading,setLoading]=useState(true);
const[error,setError]=useState('');
const[priceUpdates,setPriceUpdates]=useState({});
const[vendorIsNew,setVendorIsNew]=useState(false);
const[similarVendors,setSimilarVendors]=useState([]);
const[showVendorPicker,setShowVendorPicker]=useState(false);
const[allVendors,setAllVendors]=useState([]);
const[vendorPickerSearch,setVendorPickerSearch]=useState('');
const[resolvedVendorName,setResolvedVendorName]=useState(null);
const[newItemEC,setNewItemEC]=useState({});
const[newItemCat,setNewItemCat]=useState({});
const[matchedItems,setMatchedItems]=useState(null);

useEffect(()=>{
  if(!taskId||!orgId)return;
  (async()=>{
    // Load task
    const{data:tk}=await supabase.from('tasks').select('*').eq('id',taskId).single();
    if(!tk){setError('Task not found');setLoading(false);return;}
    setTask(tk);
    // Load staging
    const stagingId=tk.payload?.staging_id||tk.target_id;
    const{data:st}=await supabase.from('invoice_staging').select('*').eq('id',stagingId).single();
    if(!st){setError('Invoice data not found');setLoading(false);return;}
    if(st.extracted_data?.line_items)st.extracted_data.line_items=postProcessExtraction(st.extracted_data.line_items);
    setStaging(st);
    setMatchedItems(st.matched_items||[]);
    // Default price changes to approved, except risky ones
    const items=st.matched_items||[];
    const updates={};
    items.filter(i=>i.price_changed).forEach(i=>{
      if((i.amount===0||!i.amount)&&(i.qty_shipped||0)>0&&(i.unit_price||0)>0)return;
      const risky=i.cross_distributor||Math.abs(i.pct_change||0)>50;
      updates[i.matched_ingredient_id]=!risky;
    });
    setPriceUpdates(updates);
    // Check if vendor exists (by name or alias)
    if(st.vendor){
      const resolved=await resolveVendor(orgId,st.vendor);
      if(!resolved){
        setVendorIsNew(true);
        findSimilarVendors(orgId,st.vendor).then(s=>setSimilarVendors(s)).catch(()=>{});
      }else if(resolved.name.toLowerCase()!==st.vendor.trim().toLowerCase()){
        // Matched via alias — use the canonical vendor name
        setResolvedVendorName(resolved.name);
      }
    }
    setLoading(false);
  })();
},[taskId,orgId]);

async function assignToExistingVendor(v){
  try{
    await assignVendorAlias(orgId,v.id,staging.vendor);
    setResolvedVendorName(v.name);
    setVendorIsNew(false);setSimilarVendors([]);setShowVendorPicker(false);
    if(v.vendor_category==='expense'&&setReviewTaskId){setReviewTaskId(task.id);setPg('expense-invoice-review');}
  }catch(e){console.error('[InvoiceReviewPage] Alias error:',e);}
}

async function confirmAndSave(reconcileInfo){
  if(!staging||!task)return;
  setError('');
  const extracted=staging.extracted_data;
  const matched=matchedItems||staging.matched_items||[];
  const vendor=resolvedVendorName||staging.vendor;
  const invNumber=staging.invoice_number;
  const invDate=staging.invoice_date||new Date().toISOString().split('T')[0];
  const grandTotal=reconcileInfo?.editedTotal??staging.grand_total;

  try{
    const result=await sharedConfirmInvoice({
      orgId,uid,extracted,matched,vendor,invNumber,invDate,grandTotal,
      confirmPath:'tasks',reconcileInfo,priceUpdates,newItemEC,newItemCat,
      storagePath:staging.storage_path,
      ings,setIngs,setOtherExp,
      settings,mi,batches,bc,save,
      onConfirm,refreshBell,
      pendingSupersede:extracted._pendingSupersede,
    });

    if(result?.blocked==='duplicate'){
      throw new Error(`Duplicate of confirmed invoice #${result.duplicate.invoice_number||''} ($${Number(result.duplicate.grand_total||0).toFixed(2)}). Delete or supersede the existing invoice first, or reject this task.`);
    }
    if(result?.error){throw new Error(result.error);}
    const invoiceId=result.invoiceId;
    // Snapshot invoice fields to task payload before deleting staging
    const snapshot={vendor:extracted.vendor,invoice_number:extracted.invoice_number,invoice_date:extracted.invoice_date,grand_total:grandTotal,ocr_grand_total:extracted.grand_total};
    if(reconcileInfo?.reconcileOverride)snapshot.reconcile_override=true;
    if(reconcileInfo?.reconcileDiff)snapshot.reconcile_diff=reconcileInfo.reconcileDiff;
    const updatedPayload={...(task.payload||{}),invoice_snapshot:snapshot};

    // Complete the task
    const now=new Date().toISOString();
    await supabase.from('tasks').update({status:'completed',completed_at:now,completed_by:uid,payload:updatedPayload}).eq('id',task.id);
    await supabase.from('task_events').insert({org_id:orgId,task_id:task.id,event_type:'status_changed',old_value:'pending',new_value:'completed',actor_id:uid});

    // Auto-complete related anomaly tasks for this invoice
    await supabase.from('tasks').update({status:'completed',completed_at:now,completed_by:uid}).eq('org_id',orgId).eq('type','invoice_anomaly').eq('target_id',invoiceId).eq('status','pending');

    // Delete staging row
    await supabase.from('invoice_staging').delete().eq('id',staging.id);

    setPg('tasks');
  }catch(e){
    console.error('[InvoiceReviewPage] Confirm error:',e);
    setError(e.message||'Failed to save invoice');
  }
}

async function rejectInvoice(){
  if(!window.confirm('Reject and discard this invoice? The file and extracted data will be permanently deleted. This cannot be undone.'))return;
  try{
    const extracted=staging.extracted_data;
    const snapshot={vendor:extracted?.vendor,invoice_number:extracted?.invoice_number,invoice_date:extracted?.invoice_date,grand_total:extracted?.grand_total};
    const updatedPayload={...(task.payload||{}),invoice_snapshot:snapshot};
    if(staging.storage_path)await supabase.storage.from('invoices').remove([staging.storage_path]);
    await supabase.from('invoice_staging').delete().eq('id',staging.id);
    const now=new Date().toISOString();
    await supabase.from('tasks').update({status:'cancelled',completed_at:now,completed_by:uid,payload:updatedPayload}).eq('id',task.id);
    await supabase.from('task_events').insert({org_id:orgId,task_id:task.id,event_type:'status_changed',old_value:'pending',new_value:'cancelled',actor_id:uid});
    if(refreshBell)refreshBell();
    setPg('tasks');
  }catch(e){
    console.error('[InvoiceReviewPage] Reject error:',e);
    setError(e.message||'Failed to reject invoice');
  }
}

if(loading)return <div style={{textAlign:'center',padding:40,color:t.td}}>Loading invoice data...</div>;
if(error&&!staging)return <div style={{textAlign:'center',padding:40}}><div style={{color:t.ac,marginBottom:12}}>{error}</div><button onClick={()=>setPg('tasks')} style={{...sb,background:t.bl}}>Back to Tasks</button></div>;

const extracted=staging.extracted_data;
const matched=matchedItems||staging.matched_items||[];

return(<div>
{vendorIsNew&&<div style={{background:t.sf,border:`1px solid ${t.bd}`,borderLeft:`3px solid ${t.yl}`,borderRadius:8,padding:'16px 20px',marginBottom:16}}>
<div style={{fontSize:14,fontWeight:700,marginBottom:4}}>New vendor: {staging.vendor}</div>
<div style={{fontSize:12,color:t.tm,marginBottom:12}}>First time we've seen this vendor. Match to an existing vendor or classify as new.</div>
{similarVendors.length>0&&<div style={{marginBottom:12}}>
<div style={{fontSize:11,fontWeight:600,color:t.td,marginBottom:6}}>Did you mean one of these?</div>
{similarVendors.map(v=><div key={v.id} onClick={()=>assignToExistingVendor(v)} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',border:`1px solid ${t.bd}`,borderRadius:6,marginBottom:4,cursor:'pointer',background:t.cd}} onMouseOver={e=>e.currentTarget.style.background=t.sfh||t.sf} onMouseOut={e=>e.currentTarget.style.background=t.cd}>
<span style={{fontSize:12,fontWeight:500}}>{v.name}</span>
<span style={{fontSize:10,color:t.td}}>{Math.round(v.score*100)}% match</span>
</div>)}
</div>}
<div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
<button onClick={()=>setVendorIsNew(false)} style={{...sb,background:t.bl}}>New inventory vendor</button>
<button onClick={()=>{if(setReviewTaskId){setReviewTaskId(task.id);setPg('expense-invoice-review');}}} style={{...sb,background:'transparent',color:t.tm,border:`1px solid ${t.bd}`}}>New expense vendor</button>
<button onClick={async()=>{if(!allVendors.length){const v=await loadAllVendorsForUI(orgId);setAllVendors(v);}setShowVendorPicker(true);}} style={{background:'none',border:'none',color:t.bl,cursor:'pointer',fontSize:11,fontFamily:"'DM Sans',sans-serif",padding:0}}>Match to a different vendor →</button>
</div>
{showVendorPicker&&<div style={{marginTop:12,border:`1px solid ${t.bd}`,borderRadius:8,background:t.cd,padding:12,maxHeight:240,overflow:'auto'}}>
<input value={vendorPickerSearch} onChange={e=>setVendorPickerSearch(e.target.value)} placeholder="Search vendors..." style={{...si,width:'100%',marginBottom:8,boxSizing:'border-box'}} autoFocus/>
{allVendors.filter(v=>!vendorPickerSearch||v.name.toLowerCase().includes(vendorPickerSearch.toLowerCase())).slice(0,10).map(v=><div key={v.id} onClick={()=>assignToExistingVendor(v)} style={{padding:'6px 10px',cursor:'pointer',borderBottom:`1px solid ${t.bd}`,fontSize:12}} onMouseOver={e=>e.currentTarget.style.background=t.sfh||t.sf} onMouseOut={e=>e.currentTarget.style.background='transparent'}>{v.name}{v.vendor_category==='expense'&&<span style={{fontSize:9,color:t.td,marginLeft:6}}>Expense</span>}</div>)}
</div>}
</div>}
<div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
<button onClick={()=>setPg('tasks')} style={{...sb,background:'transparent',color:t.tm,padding:'4px 0',fontSize:11}}>← Back to Tasks</button>
<h1 style={{fontSize:20,fontWeight:700,margin:0}}>Review Invoice</h1>
</div>
{staging.storage_path?<InvoiceOriginalViewer storagePath={staging.storage_path} t={t}/>:<div style={{border:`1px solid ${t.bd}`,borderRadius:8,background:t.sf,padding:'10px 16px',marginBottom:16,fontSize:11,color:t.td,fontStyle:'italic'}}>Preview unavailable — no PDF stored with this invoice</div>}
<InvoiceReview extracted={extracted} matched={matched} setMatched={setMatchedItems} priceUpdates={priceUpdates} setPriceUpdates={setPriceUpdates} newItemEC={newItemEC} setNewItemEC={setNewItemEC} newItemCat={newItemCat} setNewItemCat={setNewItemCat} suggestEC={suggestEC} onConfirm={confirmAndSave} onCancel={()=>setPg('tasks')} onReject={rejectInvoice} t={t} si={si} sb={sb} sh={sh} sd={sd} mn={mn} error={error} ings={ings} setIngs={setIngs} settings={settings} save={save} orgId={orgId} uid={uid}/>
</div>);
}
